/**
 * Folder helpers (spec §8): special-use detection, display names, ordering, path getters
 * (with the `\Sent` fix from §21.2), tree building honouring `tik.collapsed`, and the
 * top-level limit of 5 with `tik.moreFolders`.
 */
import { FOLDER_NAMES_AR, getLang } from '$lib/i18n';
import type { FolderRec } from '$lib/types';
import { SNOOZED_FOLDER } from './snooze';

export const COLLAPSED_KEY = 'tik.collapsed';
export const MORE_FOLDERS_KEY = 'tik.moreFolders';
export const TOP_LEVEL_LIMIT = 5;

export type FolderIcon = 'inbox' | 'send' | 'draft' | 'moon' | 'archive' | 'junk' | 'trash' | 'list' | 'folder';

const ICONS: Record<string, FolderIcon> = {
  '\\Inbox': 'inbox',
  '\\Sent': 'send',
  '\\Drafts': 'draft',
  '\\Junk': 'junk',
  '\\Trash': 'trash',
  '\\Archive': 'archive',
  '\\All': 'list',
  '\\Snoozed': 'moon',
};

const ORDER: Record<string, number> = {
  '\\Inbox': 0,
  '\\Sent': 1,
  '\\Drafts': 2,
  '\\Snoozed': 3,
  '\\Archive': 4,
  '\\Junk': 5,
  '\\Trash': 6,
};

export interface PathContext {
  archivePath: string | null;
  snoozePath: string | null;
}

/** `specialUse ?? \Archive (by archive path) ?? \Snoozed (by snooze path) ?? null`. */
export function specialUseOf(f: Pick<FolderRec, 'path' | 'specialUse'>, ctx: PathContext): string | null {
  return f.specialUse ?? (f.path === ctx.archivePath ? '\\Archive' : null) ?? (f.path === ctx.snoozePath ? '\\Snoozed' : null);
}

/** Arabic table for special folders when lang=ar; otherwise the server name. */
export function displayName(f: FolderRec, ctx: PathContext): string {
  if (getLang() !== 'ar') return f.name;
  return FOLDER_NAMES_AR[specialUseOf(f, ctx) ?? ''] ?? f.name;
}

export function folderIcon(f: FolderRec, ctx: PathContext): FolderIcon {
  return ICONS[specialUseOf(f, ctx) ?? ''] ?? 'folder';
}

/** Rail order: specials 0–6, everything else 99. */
export function folderOrder(f: FolderRec, ctx: PathContext): number {
  return ORDER[specialUseOf(f, ctx) ?? ''] ?? 99;
}

const byPathOrName = (folders: readonly FolderRec[], name: string) =>
  folders.find((f) => f.path.toLowerCase() === name || f.name.toLowerCase() === name)?.path ?? null;

export function trashPath(folders: readonly FolderRec[]): string | null {
  return folders.find((f) => f.specialUse === '\\Trash')?.path ?? byPathOrName(folders, 'trash');
}

export function archivePath(folders: readonly FolderRec[]): string | null {
  return folders.find((f) => f.specialUse === '\\Archive')?.path ?? byPathOrName(folders, 'archive');
}

/** Junk is matched by special-use only. */
export function junkPath(folders: readonly FolderRec[]): string | null {
  return folders.find((f) => f.specialUse === '\\Junk')?.path ?? null;
}

/** The snoozed folder has no special-use flag; matched by path `Snoozed` or name `snoozed`. */
export function snoozePath(folders: readonly FolderRec[]): string | null {
  return folders.find((f) => f.path === SNOOZED_FOLDER || f.name.toLowerCase() === 'snoozed')?.path ?? null;
}

/** Fixed: matches special-use `\Sent` (the legacy compared the bare word `Sent`). */
export function sentPath(folders: readonly FolderRec[]): string | null {
  return folders.find((f) => f.specialUse === '\\Sent')?.path ?? byPathOrName(folders, 'sent');
}

export function draftsPath(folders: readonly FolderRec[]): string | null {
  return folders.find((f) => f.specialUse === '\\Drafts')?.path ?? byPathOrName(folders, 'drafts');
}

export function inboxPath(folders: readonly FolderRec[]): string {
  return folders.find((f) => f.specialUse === '\\Inbox')?.path ?? folders.find((f) => f.path.toUpperCase() === 'INBOX')?.path ?? 'INBOX';
}

export function pathContext(folders: readonly FolderRec[]): PathContext {
  return { archivePath: archivePath(folders), snoozePath: snoozePath(folders) };
}

/** Custom = no special use, not INBOX, not the archive/snooze path. */
export function isCustomFolder(f: FolderRec, ctx: PathContext): boolean {
  return folderOrder(f, ctx) === 99 && f.path.toUpperCase() !== 'INBOX' && !f.specialUse;
}

/** Server delimiter: the first folder that reports one, default `/`. */
export function delimiterOf(folders: readonly FolderRec[]): string {
  return folders.find((f) => f.delimiter)?.delimiter || '/';
}

/** Split into rail sections: specials by order, customs by localised name. */
export function splitFolders(folders: readonly FolderRec[], ctx: PathContext): { special: FolderRec[]; custom: FolderRec[] } {
  const loc = getLang() === 'ar' ? 'ar' : 'en';
  const all = [...folders];
  const special = all.filter((f) => folderOrder(f, ctx) < 99).sort((a, b) => folderOrder(a, ctx) - folderOrder(b, ctx));
  const custom = all.filter((f) => folderOrder(f, ctx) === 99).sort((a, b) => a.name.localeCompare(b.name, loc));
  return { special, custom };
}

export interface FolderNode {
  folder: FolderRec;
  depth: number;
  children: FolderNode[];
  subtreeUnseen: number;
  hasChildren: boolean;
}

/** Build a tree from paths using `delimiter`; roots keep server order. */
export function buildFolderTree(folders: readonly FolderRec[], delimiter = '/'): FolderNode[] {
  const nodes = new Map<string, FolderNode>();
  for (const f of folders) nodes.set(f.path, { folder: f, depth: 0, children: [], subtreeUnseen: f.unseen ?? 0, hasChildren: false });
  const roots: FolderNode[] = [];
  for (const n of nodes.values()) {
    const p = n.folder.path;
    const i = p.lastIndexOf(delimiter);
    const parent = i > 0 ? nodes.get(p.slice(0, i)) : undefined;
    if (parent) {
      parent.children.push(n);
      parent.hasChildren = true;
    } else roots.push(n);
  }
  const walk = (n: FolderNode, depth: number): number => {
    n.depth = depth;
    let sum = n.folder.unseen ?? 0;
    for (const c of n.children) sum += walk(c, depth + 1);
    n.subtreeUnseen = sum;
    return sum;
  };
  for (const r of roots) walk(r, 0);
  return roots;
}

/** Depth-first flatten, skipping the subtrees of collapsed paths; siblings sorted by `cmp`. */
export function flattenTree(
  roots: readonly FolderNode[],
  collapsed: ReadonlySet<string>,
  cmp: (a: FolderRec, b: FolderRec) => number = () => 0,
): FolderNode[] {
  const out: FolderNode[] = [];
  const walk = (list: readonly FolderNode[]) => {
    for (const n of [...list].sort((a, b) => cmp(a.folder, b.folder))) {
      out.push(n);
      if (!collapsed.has(n.folder.path)) walk(n.children);
    }
  };
  walk(roots);
  return out;
}

/** The rail's flattened custom-folder list, sorted by `path.localeCompare` in the UI language. */
export function customFolderRows(custom: readonly FolderRec[], collapsed: ReadonlySet<string>, delimiter: string): FolderNode[] {
  const loc = getLang() === 'ar' ? 'ar' : 'en';
  return flattenTree(buildFolderTree(custom, delimiter), collapsed, (a, b) => a.path.localeCompare(b.path, loc));
}

export interface LimitOptions {
  showAll: boolean;
  currentFolder: string;
  delimiter: string;
  limit?: number;
}

/**
 * Keep only the first `limit` top-level custom folders (and their subtrees) unless `showAll`;
 * the current folder's top-level ancestor is always kept. Returns the hidden top-level count.
 */
export function limitTopLevel(rows: readonly FolderNode[], opts: LimitOptions): { rows: FolderNode[]; hidden: number } {
  const limit = opts.limit ?? TOP_LEVEL_LIMIT;
  const tops = rows.filter((n) => n.depth === 0).map((n) => n.folder.path);
  if (opts.showAll || tops.length <= limit) return { rows: [...rows], hidden: 0 };
  const keep = new Set(tops.slice(0, limit));
  const cur = rows.find((n) => n.folder.path === opts.currentFolder);
  if (cur) {
    const anc = tops.find((tp) => cur.folder.path === tp || cur.folder.path.startsWith(tp + opts.delimiter));
    if (anc) keep.add(anc);
  }
  const visible = rows.filter((n) => [...keep].some((tp) => n.folder.path === tp || n.folder.path.startsWith(tp + opts.delimiter)));
  return { rows: visible, hidden: Math.max(0, tops.length - visible.filter((n) => n.depth === 0).length) };
}

/** Leaf name of a path (`INBOX.Work.Q3` → `Q3`), falling back to `name` then the path. */
export function leafName(path: string, name = '', delimiter = '/'): string {
  const i = path.lastIndexOf(delimiter);
  return i > 0 ? path.slice(i + delimiter.length) : name || path;
}

/** New full path after renaming the leaf of `path` to `leaf`. */
export function renamedPath(path: string, leaf: string, delimiter: string): string {
  const i = path.lastIndexOf(delimiter);
  return (i > 0 ? path.slice(0, i) + delimiter : '') + leaf.trim();
}

/** Rewrite collapsed entries under a renamed subtree. */
export function rewriteCollapsed(collapsed: ReadonlySet<string>, oldPath: string, newPath: string, delimiter: string): Set<string> {
  const out = new Set<string>();
  for (const p of collapsed) {
    out.add(p === oldPath || p.startsWith(oldPath + delimiter) ? newPath + p.slice(oldPath.length) : p);
  }
  return out;
}

export function readCollapsed(): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]');
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function writeCollapsed(set: ReadonlySet<string>): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore storage failures */
  }
}

export function readMoreFolders(): boolean {
  try {
    return localStorage.getItem(MORE_FOLDERS_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeMoreFolders(on: boolean): void {
  try {
    localStorage.setItem(MORE_FOLDERS_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Server rule for new folder names (`POST /api/folders`). */
export const FOLDER_NAME_RE = /^[\p{L}\p{M}\p{N} _-]{1,40}$/u;
